<template>
  <div>
    <div class="pb-6 border-brand-sand border-b-2">
      <p class="title mb-2 capitalize">Text Overrides</p>
      <p class="subtitle mt-2 text-lg font-medium text-brand-brown inline">Change default and add multi-lingual support.</p>
      <p class="subtitle text-brand-blue text-lg font-semibold mt-1 inline">
        <a href="https://docs.churnkey.co/installing-churnkey#0b10e8b130b8488da2f7ef5cdd7c8b19" target="_blank">
          Learn more about text overrides and internationalization →
        </a>
      </p>
    </div>
    <div class="space-y-6 brand-input mt-6">
      <!-- Language Select -->
      <div>
        <p class="text-sm font-semibold mb-2 text-brand-black">Select Language</p>
        <div class="flex items-center space-x-2">
          <el-select filterable v-model="selectedLanguage">
            <el-option v-for="language in languages" :key="language.value" :label="language.label" :value="language.value" />
          </el-select>
          <!-- Option to add another language, el-tooltip explaining that it will add the language -->
          <el-tooltip content="Add a new language" placement="top">
            <el-button size="small" type="primary" class="px-4 rounded-md border-0" @click="openAddLanguagePrompt">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                class="transform rotate-45 w-6 h-6 fill-current text-white pl-1.5 pt-1.5 align-center content-center"
              >
                <path
                  d="M11.9012 9.18844L8.71214 5.99968L11.9002 2.81125C11.9316 2.77984 11.9566 2.74256 11.9735 2.70153C11.9905 2.66049 11.9993 2.61651 11.9993 2.5721C11.9993 2.52768 11.9905 2.48371 11.9735 2.44267C11.9566 2.40164 11.9316 2.36436 11.9002 2.33295L9.66687 0.0990798C9.60342 0.0356394 9.51736 0 9.42763 0C9.33791 0 9.25185 0.0356394 9.1884 0.0990798L5.9998 3.28751L2.81172 0.0990798C2.68487 -0.0279336 2.46026 -0.0279336 2.33325 0.0990798L0.0993791 2.33261C0.0360219 2.39611 0.000439925 2.48214 0.000439925 2.57184C0.000439925 2.66154 0.0360219 2.74758 0.0993791 2.81108L3.28798 5.99968L0.098869 9.18861C0.0355532 9.25213 0 9.33816 0 9.42785C0 9.51753 0.0355532 9.60356 0.098869 9.66708L2.3324 11.9008C2.3638 11.9322 2.40109 11.9572 2.44214 11.9742C2.48319 11.9912 2.52719 12 2.57163 12C2.61607 12 2.66008 11.9912 2.70113 11.9742C2.74218 11.9572 2.77947 11.9322 2.81087 11.9008L5.9998 8.71184L9.18891 11.9006C9.25505 11.9666 9.34126 11.9997 9.42814 11.9997C9.51503 11.9997 9.60141 11.9666 9.66755 11.9006L11.9014 9.66691C11.9648 9.60343 12.0004 9.51737 12.0004 9.42764C12.0004 9.33791 11.9647 9.25187 11.9012 9.18844Z"
                ></path>
              </svg>
            </el-button>
          </el-tooltip>
        </div>
        <div class="mt-6 rounded-md px-4 py-4 bg-brand-light-sand flex items-center space-x-3" v-if="!supportSelectedLanguage">
          <font-awesome-icon :icon="['fas', 'comment-dots']" class="text-gray-800" />
          <p class="text-sm text-gray-800 font-medium">
            We do not have default translations for <span class="font-semibold"> {{ selectedLanguageLabel }}</span
            >. You will need to add all translations for this language below.
          </p>
        </div>
      </div>
      <br />
      <!-- All Translations for selected language -->
      <div class="space-y-2 max-w-full">
        <p class="text-sm font-semibold text-brand-black">Translations for {{ selectedLanguageLabel }}</p>
        <div class="flex items-center justify-between">
          <el-radio-group v-model="showAll" size="small">
            <el-radio-button :label="true">Show All</el-radio-button>
            <el-radio-button :label="false">Show Overrides Only</el-radio-button>
          </el-radio-group>
          <!-- Save Button -->
          <div class="flex items-center space-x-4">
            <el-button v-if="saveState === SAVE_STATE.UNSAVED" type="text" @click="discardChanges" class="font-semibold mr-1.5">
              Discard All Changes
            </el-button>
            <SaveStateTag v-else :status="saveState" />
            <el-button
              v-can-enable="'cf:write'"
              size="small"
              type="primary"
              @click="pushOrgTranslations"
              :loading="saveState === SAVE_STATE.SAVING"
              :disabled="saveState !== SAVE_STATE.UNSAVED"
            >
              <span class="font-bold"> Save All Changes </span>
            </el-button>
          </div>
        </div>
        <br />
        <table v-if="displayedKeyList.length" class="w-full divide-white">
          <thead>
            <tr>
              <th scope="col" class="whitespace-nowrap py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-0">Text Tag</th>
              <th scope="col" class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900">Your Override</th>
              <th scope="col" class="whitespace-nowrap px-2 py-3.5 text-left text-sm font-semibold text-gray-900 w-28 pl-6">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="key in displayedKeyList" :key="key">
              <td class="whitespace-nowrap py-2 pl-4 pr-3 text-sm text-brand-brown sm:pl-0 w-32 overflow-ellipsis font-mono font-medium">
                {{ key }}
              </td>
              <td class="whitespace-nowrap px-2 py-2 text-sm font-medium text-gray-900">
                <el-input
                  size="small"
                  :value="combinedTranslations[selectedLanguage][key]"
                  @input="(v) => updateTranslation(selectedLanguage, key, v)"
                  :placeholder="(defaultMessages[selectedLanguage] && defaultMessages[selectedLanguage][key]) || defaultMessages.en[key]"
                >
                </el-input>
              </td>
              <!-- <td class="whitespace-nowrap px-2 py-2 text-sm font-medium text-gray-900">{{ combinedTranslations[selectedLanguage][key] }}</td> -->
              <td class="whitespace-nowrap px-2 py-2 text-sm text-gray-900 w-28">
                <el-button type="text" class="px-4" size="small" @click="resetTranslationToDefault(selectedLanguage, key)">
                  <span class="font-semibold"> Reset to Default </span>
                </el-button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- Add New Selected Language Translation -->
      <div v-if="!showAll">
        <p class="text-sm font-semibold mb-2 text-gray-700">Add New Override for {{ selectedLanguageLabel }}</p>
        <el-select size="small" filterable :multiple="false" value="" @change="(key) => addTranslation(key)">
          <el-option v-for="option in displayedLanguageTranslationOptions" :key="option.value" :label="option.label" :value="option.value">
            <div class="pt-2">
              <p class="mr-4 text-brand-blue font-medium text-xs float-left max-w-sm overflow-ellipsis">
                {{ option.value }}
              </p>
              <p class="text-gray-600 text-xs mr-6 flex-grow float-right max-w-sm overflow-ellipsis">
                {{ option.label }}
              </p>
            </div>
          </el-option>
        </el-select>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { FETCH_ORG_PRICES, PLAN_SETTINGS_SCHEDULE_SAVE } from '@/store/action-types';
import { mapGetters } from 'vuex';
import SaveStateTag from '@/components/UI/SaveStateTag.vue';
import { SAVE_STATE } from '@/store/shared';
import { defaultMessages } from '@/helpers/translations';
import { languageLabels, defaultLanguages } from '@/helpers/languages';
import { SET_ORG_DETAILS } from '../../store/mutation-types';
import OrgService from '../../api/org.service';

export default Vue.extend({
  name: 'OrgTranslationEditor',
  components: {
    SaveStateTag,
  },
  data() {
    return {
      saveState: SAVE_STATE.SAVED,
      selectedLanguage: 'en',
      SAVE_STATE,
      defaultMessages,
      showAll: true,
    };
  },
  computed: {
    ...mapGetters(['orgTranslations']),
    selectedLanguageDetails() {
      return this.selectedLanguage && this.languages.find((lang) => lang.value === this.selectedLanguage);
    },
    supportSelectedLanguage() {
      return defaultLanguages.includes(this.selectedLanguage);
    },
    combinedTranslations() {
      // combine defaults with org translations
      const combined = {};
      Object.keys(this.orgTranslations).forEach((lang) => {
        combined[lang] = { ...defaultMessages[lang], ...this.orgTranslations[lang] };
      });
      return combined;
    },
    languages() {
      return Object.keys(this.orgTranslations).map((lang) => ({ value: lang, label: languageLabels[lang]?.englishName || lang }));
    },
    selectedLanguageLabel() {
      return languageLabels[this.selectedLanguage]?.englishName
        ? `${languageLabels[this.selectedLanguage]?.englishName} (${this.selectedLanguage})`
        : this.selectedLanguage;
    },
    keyList() {
      return Object.keys(defaultMessages.en);
    },
    displayedLanguageTranslations() {
      const obj = this.orgTranslations[this.selectedLanguage] || {};
      return Object.keys(obj).map((key) => ({ key, value: obj[key] }));
    },
    displayedLanguageTranslationOptions() {
      return (
        this.keyList
          .map((key) => ({
            value: key,
            label: defaultMessages[this.selectedLanguage]?.[key],
          }))
          // filter out any translations that are already in use
          .filter(({ key }) => this.orgTranslations[this.selectedLanguage]?.[key] === undefined)
      );
    },
    displayedKeyList() {
      if (this.showAll) {
        return this.keyList;
      }
      return this.keyList.filter((key) => this.orgTranslations[this.selectedLanguage]?.[key] !== undefined);
    },
  },
  methods: {
    async pushOrgTranslations() {
      const newTranslations = { ...this.orgTranslations };
      this.setOrgTranslations(newTranslations);
      this.saveState = SAVE_STATE.SAVING;
      try {
        await OrgService.update(this.$store.state.org._id, { translations: newTranslations });
        this.saveState = SAVE_STATE.SAVED;
      } catch (err) {
        this.saveState = SAVE_STATE.ERROR;
      }
    },
    setOrgTranslations(translations) {
      this.saveState = SAVE_STATE.UNSAVED;
      const newTranslations = { ...translations };
      // remove any empty translations
      Object.keys(newTranslations).forEach((lang) => {
        Object.keys(newTranslations[lang]).forEach((key) => {
          if (newTranslations[lang][key] === '') {
            delete newTranslations[lang][key];
          }
        });
      });
      this.$store.commit(SET_ORG_DETAILS, { translations });
    },
    addLanguage(language) {
      const newTranslations = { ...this.orgTranslations, [language]: {} };
      this.setOrgTranslations(newTranslations);
      this.selectedLanguage = language;
    },
    removeLanguage(language) {
      const newTranslations = { ...this.orgTranslations };
      delete newTranslations[language];
      this.setOrgTranslations(newTranslations);
    },
    addTranslation(key) {
      const value = defaultMessages[this.selectedLanguage]?.[key] || defaultMessages.en[key];
      this.updateTranslation(this.selectedLanguage, key, value);
    },
    updateTranslation(language, key, value) {
      const newTranslations = { ...this.orgTranslations };
      newTranslations[language][key] = value;
      this.setOrgTranslations(newTranslations);
    },
    removeTranslation(language, key) {
      const newTranslations = { ...this.orgTranslations };
      delete newTranslations[language][key];
      this.setOrgTranslations(newTranslations);
    },
    resetTranslationToDefault(language, key) {
      const newTranslations = { ...this.orgTranslations };
      delete newTranslations[language][key];
      this.setOrgTranslations(newTranslations);
    },
    openAddLanguagePrompt() {
      this.$prompt('Enter the language code for the new language', 'Add Language', {
        confirmButtonText: 'Add',
        cancelButtonText: 'Cancel',
        // input pattern is language, 2 to 7 characters of letters and dash
        inputPattern: /^[a-z]{2,4}(-[a-z]{2,4})?$/i,
        inputErrorMessage: 'Language code must be 2 to 7 characters of letters and dash.',
      })
        .then(({ value }) => {
          // check if language already exists
          if (this.orgTranslations[value]) {
            this.$message({
              type: 'error',
              message: 'Language already exists.',
            });
            return;
          }
          this.addLanguage(value);
        })
        .catch(() => {
          // do nothing
        });
    },
    discardChanges() {
      // refresh the page for now
      window.location.reload();
      //       const state = this.$store.state as IState;
      // this.loadingCampaign = true;
      // await this.$store.dispatch(FETCH_DUNNING_CAMPAIGN_BLUEPRINT, state.currentCampaignBlueprintId);
      // if (!state.currentEmailBlueprintId || !this.currentCampaignEmailBlueprintMap[state.currentEmailBlueprintId]) {
      //   this.$store.commit(SET_CURRENT_DUNNING_EMAIL_BLUEPRINT_ID, this.currentCampaignEmailBlueprints[0].guid);
      // }
      // this.loadingCampaign = false;
    },
  },
});
</script>
